/************************************************************************************

* [PAA pkg: filter-drawer] — MOBILE FILTER DRAWER POLISH (handoff issue #1)
* Three presentation-layer features, no filtering logic:
*
* 1. LIVE RESULT COUNT on the drawer Apply button — "Apply · N items"
* 2. JUDGE.ME BADGE REHYDRATION after every filter AJAX re-render
* 3. RESET clears PENDING (checked but un-applied) selections too

*************************************************************************************/
;(() => {
	// Theme editor re-injects section scripts on re-render — bind once.
	if (window.__paaFilterDrawerCountInit) return
	window.__paaFilterDrawerCountInit = true

	const FOOTER_ID = 'CollectionFiltersControls-drawer-all'

	// The footer's innerHTML is replaced after every AJAX apply
	// (LTACollectionFilters.renderContent), so re-query on each use.
	const getFooterEl = () => document.getElementById(FOOTER_ID)

	const getCountEl = () => {
		const footerEl = getFooterEl()
		return footerEl ? footerEl.querySelector('[data-results-count]') : null
	}

	const getDrawerFiltersEl = () => document.querySelector('l-collection-filters[data-type="drawer"]')

	/*********************************************************************
	 * 1. LIVE RESULT COUNT ON THE APPLY BUTTON
	 *
	 * The LTA filter system (assets/A-main-collection.js) dispatches a
	 * debounced `l:filters:input` event with the pending URL params every
	 * time an input inside a filter form changes. When the source form is
	 * the drawer's, we fetch the same `?section_id=` render that pressing
	 * Apply would fetch, read the fresh [data-results-count] label out of
	 * it, and mirror it into the sticky footer — so shoppers see
	 * "Apply · N items" BEFORE they commit (and never land on an
	 * unexpected zero-result grid). Responses are cached per URL and
	 * in-flight requests are aborted, mirroring LTACollectionFilters.
	 *********************************************************************/
	const cache = new Map()
	let abortController = null

	// Snapshot of the APPLIED-state count markup so Reset can restore it
	// without a fetch (the footer HTML always renders the applied count).
	let appliedCountHTML = null
	const captureAppliedCount = () => {
		const countEl = getCountEl()
		if (countEl) appliedCountHTML = countEl.innerHTML
	}
	captureAppliedCount()

	const parseSectionDOM = (html) =>
		typeof l_getSectionDOM === 'function'
			? l_getSectionDOM(html)
			: new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section')

	document.addEventListener('l:filters:input', (event) => {
		const filtersEl = event.target

		// Only react to the drawer's form (quick-filter pills apply instantly and
		// re-render the footer themselves via l:filters:rendered).
		if (!filtersEl || !filtersEl.dataset || filtersEl.dataset.type !== 'drawer') return
		if (!event.detail || typeof event.detail.newSearchParams !== 'string') return

		const countEl = getCountEl()
		if (!countEl) return

		const url = `${window.location.pathname}?section_id=${filtersEl.dataset.sectionId}&${event.detail.newSearchParams}`

		if (cache.has(url)) {
			countEl.innerHTML = cache.get(url)
			return
		}

		countEl.classList.add('is-loading')

		abortController?.abort()
		abortController = new AbortController()

		fetch(url, { signal: abortController.signal })
			.then((response) => response.text())
			.then((html) => {
				const sectionDOM = parseSectionDOM(html)
				const newCountEl = sectionDOM ? sectionDOM.querySelector('[data-results-count]') : null

				if (!newCountEl) return

				cache.set(url, newCountEl.innerHTML)

				const liveCountEl = getCountEl()
				if (liveCountEl) liveCountEl.innerHTML = newCountEl.innerHTML
			})
			.catch(() => {
				/* aborted / offline — keep the previous count */
			})
			.finally(() => {
				const liveCountEl = getCountEl()
				if (liveCountEl) liveCountEl.classList.remove('is-loading')
			})
	})

	/*********************************************************************
	 * 2. JUDGE.ME BADGE REHYDRATION (QA issue #3)
	 *
	 * Judge.me hydrates .jdgm-preview-badge nodes once on page load and
	 * tags them jdgm--done-setup. Product cards injected by the filter
	 * AJAX flow (LTACollectionGrid swaps the grid innerHTML on
	 * l:filters:rendered) arrive un-hydrated, so review counts render as
	 * raw wrapped "N reviews" text instead of the compact star display.
	 * jdgm.customizeBadges() is the loader's own idempotent entry point:
	 * it only processes badges WITHOUT jdgm--done-setup (verified against
	 * the live loader — window.jdgmSetup does NOT exist on this store).
	 * Optional chaining: if Judge.me is blocked/absent nothing throws.
	 *
	 * Registration order guarantees LTACollectionGrid's own
	 * l:filters:rendered listener (registered when A-main-collection.js
	 * defines the element, before this deferred script runs) has already
	 * swapped the grid; the setTimeout is belt-and-braces so hydration
	 * always follows the swap even if that order ever changes.
	 *********************************************************************/
	const rehydrateJudgeMeBadges = () => {
		setTimeout(() => window.jdgm?.customizeBadges?.(), 0)
	}

	document.addEventListener('l:filters:rendered', () => {
		rehydrateJudgeMeBadges()
		// Fresh footer HTML == applied state; re-snapshot for Reset (below).
		captureAppliedCount()
	})

	// Same bug, same page: the "Load More" pagination (LTACollectionPagination)
	// appends fetched card HTML without dispatching l:filters:rendered. Watch
	// the grid for appended children and hydrate those too (idempotent — see
	// above). Subtree changes are ignored; only child-list swaps re-trigger.
	const gridEl = document.getElementById('main-product-grid')
	if (gridEl && typeof MutationObserver === 'function') {
		new MutationObserver(rehydrateJudgeMeBadges).observe(gridEl, { childList: true })
	}

	/*********************************************************************
	 * 3. RESET ALSO CLEARS PENDING SELECTIONS (QA issue #4)
	 *
	 * CRO Gurus render the drawer's Reset link disabled (is-disabled +
	 * disabled/aria-hidden/tabindex) whenever the PAGE has no applied
	 * filters — so on an unfiltered page with pending (checked but
	 * un-applied) boxes, tapping Reset did nothing. Now:
	 *  - any input inside the drawer re-evaluates: Reset is enabled when
	 *    something is pending OR applied, disabled only when neither.
	 *  - clicking Reset ALSO reverts the form to its server-rendered
	 *    (= applied) state client-side. The existing LTA handler keeps
	 *    clearing APPLIED filters via l:filters:triggeredFilter; with
	 *    pending-only changes that dispatch is a no-op (params unchanged),
	 *    which is exactly why the client-side clear is needed.
	 *********************************************************************/
	const getResetEl = () => {
		const footerEl = getFooterEl()
		return footerEl ? footerEl.querySelector('a') : null
	}

	// Pending = any control that differs from its server-rendered default
	// (the defaults ARE the applied state: the drawer HTML re-renders from
	// the server after every apply).
	const formHasPendingChanges = (formEl) =>
		Array.from(formEl.elements).some((el) => {
			if (el.type === 'checkbox' || el.type === 'radio') return el.checked !== el.defaultChecked
			if (el.tagName === 'INPUT' && el.type !== 'hidden') return el.value !== el.defaultValue
			return false
		})

	const pageHasAppliedFilters = () => /(\?|&)filter\./.test(window.location.search)

	const setResetDisabled = (resetEl, disabled) => {
		resetEl.classList.toggle('is-disabled', disabled)
		if (disabled) {
			resetEl.setAttribute('disabled', '')
			resetEl.setAttribute('aria-hidden', 'true')
			resetEl.setAttribute('tabindex', '-1')
		} else {
			resetEl.removeAttribute('disabled')
			resetEl.removeAttribute('aria-hidden')
			resetEl.removeAttribute('tabindex')
		}
	}

	const syncResetState = () => {
		const resetEl = getResetEl()
		const filtersEl = getDrawerFiltersEl()
		const formEl = filtersEl ? filtersEl.querySelector('form') : null
		if (!resetEl || !formEl) return

		setResetDisabled(resetEl, !formHasPendingChanges(formEl) && !pageHasAppliedFilters())
	}

	// Instant (not debounced) enable/disable as the shopper taps options.
	document.addEventListener('input', (event) => {
		if (!event.target.closest || !event.target.closest('l-collection-filters[data-type="drawer"]')) return
		syncResetState()
	})

	document.addEventListener('click', (event) => {
		const footerEl = getFooterEl()
		if (!footerEl || !footerEl.contains(event.target)) return

		const resetEl = getResetEl()
		if (!resetEl || !resetEl.contains(event.target)) return

		const filtersEl = getDrawerFiltersEl()
		const formEl = filtersEl ? filtersEl.querySelector('form') : null
		if (!formEl || !formHasPendingChanges(formEl)) return

		// Revert every control to its server-rendered (applied) default.
		formEl.reset()

		// form.reset() doesn't move the price-range slider thumbs (their
		// --from/--to CSS vars are set imperatively by LTAPriceRange) —
		// re-sync them from the text inputs it just restored.
		formEl.querySelectorAll('l-price-range').forEach((priceRangeEl) => {
			const rangeMin = priceRangeEl.querySelector('input[type="range"]:first-child')
			const rangeMax = priceRangeEl.querySelector('input[type="range"]:last-child')
			const inputMin = priceRangeEl.querySelector('input[name="filter.v.price.gte"]')
			const inputMax = priceRangeEl.querySelector('input[name="filter.v.price.lte"]')
			if (!rangeMin || !rangeMax) return

			rangeMin.value = inputMin && inputMin.value !== '' ? inputMin.value : rangeMin.min
			rangeMax.value = inputMax && inputMax.value !== '' ? inputMax.value : rangeMax.max
			rangeMin.parentElement.style.setProperty('--from', `${(parseInt(rangeMin.value) / parseInt(rangeMin.max)) * 100}%`)
			rangeMax.parentElement.style.setProperty('--to', `${(parseInt(rangeMax.value) / parseInt(rangeMax.max)) * 100}%`)
		})

		// Refresh each tab's "+N" count / selected-sort label. Non-bubbling
		// on purpose: LTA's updateFilterCount listens on the tab itself, and
		// staying below the form avoids re-triggering the debounced
		// l:filters:input flow (which would re-enable Apply for a no-op).
		formEl.querySelectorAll('l-collapsible-tab').forEach((tabEl) => {
			tabEl.dispatchEvent(new Event('input'))
		})

		// Nothing is pending anymore: Apply goes back to disabled with the
		// applied-state count; Reset stays enabled only if filters are applied
		// (in which case the LTA reset flow is re-rendering the section now).
		const submitBtnEl = footerEl.querySelector('button[type="submit"]')
		if (submitBtnEl) {
			submitBtnEl.classList.add('is-disabled')
			submitBtnEl.disabled = true
		}

		const countEl = getCountEl()
		if (countEl && appliedCountHTML) countEl.innerHTML = appliedCountHTML

		syncResetState()
	})
})()
